/**
 * Bet breakdown — Backend-UI `bet-breakdown` filter + table clone.
 * Filters: member (multi), From/To date, Currency, Max Row, Search, Clear Date,
 * Invalid Bet, Bet Count. Table includes IP like Backend-UI CM/User view.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Icon, Modal, Pager, Popover, useToast } from '@w7/ui'
import {
  useInvalidateWorkstationBets,
  useRevalidateWorkstationBet,
  type WorkstationBetFilters,
} from '@/api/hooks/net-exposure/useWorkstation'
import { useCmMe } from '@/api/hooks/useCmMe'
import { money } from '@/lib/format'
import {
  betListMatchedUnmatchedAmt,
  betListProfitLiabilityCurrency,
} from '@/features/net-exposure/betListMatchedAmt'
import {
  canManageWorkstationInvalidBets,
  canRevalidateWorkstationBet,
  canSelectBetForInvalid,
} from './access'
import {
  formatFancyRuns,
  formatLineSessionRunsForSide,
  isLineSessionBetListMarket,
  workstationOddsMatched,
  workstationOddsPlaced,
} from './lineSessionBetOdds'
import {
  defaultBetDateRange,
  filterWorkstationBets,
  fromDateInputValue,
  getWorkstationIpDetail,
  isoFromLocalParts,
  localPartsFromIso,
  paginateBets,
  toDateInputValue,
  type LocalDateTimeParts,
  type WorkstationBet,
  type WorkstationBook,
  type WorkstationIpDetail,
} from '../workstation-fixture'

const CURRENCIES = ['All', 'INR', 'POINTS'] as const
/** Backend-UI workstation.js `$scope.maxRows` */
const PAGE_SIZES = [100, 200, 500, 1000] as const

const HOUR_OPTS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'))
const MIN_SEC_OPTS = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'))

function BetDateTimeField({
  label,
  iso,
  draft,
  endOfDay,
  onDraftChange,
  onCommitIso,
  onBlurDraft,
}: {
  label: string
  iso: string
  draft: string
  endOfDay: boolean
  onDraftChange: (raw: string) => void
  onCommitIso: (nextIso: string) => void
  onBlurDraft: () => void
}) {
  const [open, setOpen] = useState(false)
  const anchor = useRef<HTMLButtonElement>(null)
  const [parts, setParts] = useState<LocalDateTimeParts>(
    () => localPartsFromIso(iso) ?? { date: '', hour: 0, minute: 0, second: 0 },
  )

  useEffect(() => {
    if (!open) return
    const next = localPartsFromIso(iso)
    if (next) setParts(next)
  }, [open, iso])

  const commitParts = (next: LocalDateTimeParts) => {
    setParts(next)
    const out = isoFromLocalParts(next)
    if (out) onCommitIso(out)
  }

  return (
    <div className="w7-ws-bets-field w7-ws-bets-field--datetime">
      <span>{label}</span>
      <span className="w7-ws-bets-date">
        <input
          type="text"
          inputMode="numeric"
          spellCheck={false}
          placeholder="DD.MM.YYYY HH:mm:ss"
          aria-label={label}
          value={draft}
          onChange={(e) => {
            const raw = e.target.value
            onDraftChange(raw)
            const next = fromDateInputValue(raw, endOfDay)
            if (next) onCommitIso(next)
          }}
          onBlur={onBlurDraft}
        />
        <button
          ref={anchor}
          type="button"
          className="w7-ws-bets-date-cal"
          aria-label={`Open ${label} calendar`}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <Icon name="calendar" size={16} />
        </button>
        <Popover
          open={open}
          onClose={() => setOpen(false)}
          anchorRef={anchor}
          label={label}
          width={260}
        >
          <div className="w7-ws-bets-dt-picker">
            <label className="w7-ws-bets-dt-row">
              <span>Date</span>
              <input
                type="date"
                value={parts.date}
                onChange={(e) => commitParts({ ...parts, date: e.target.value })}
              />
            </label>
            <div className="w7-ws-bets-dt-row">
              <span>Time (24h)</span>
              <div className="w7-ws-bets-dt-clock">
                <select
                  aria-label={`${label} hour`}
                  value={String(parts.hour).padStart(2, '0')}
                  onChange={(e) => commitParts({ ...parts, hour: Number(e.target.value) })}
                >
                  {HOUR_OPTS.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
                <span>:</span>
                <select
                  aria-label={`${label} minute`}
                  value={String(parts.minute).padStart(2, '0')}
                  onChange={(e) => commitParts({ ...parts, minute: Number(e.target.value) })}
                >
                  {MIN_SEC_OPTS.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
                <span>:</span>
                <select
                  aria-label={`${label} second`}
                  value={String(parts.second).padStart(2, '0')}
                  onChange={(e) => commitParts({ ...parts, second: Number(e.target.value) })}
                >
                  {MIN_SEC_OPTS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <Button type="button" variant="primary" size="sm" onClick={() => setOpen(false)}>
              Done
            </Button>
          </div>
        </Popover>
      </span>
    </div>
  )
}

function formatWhen(iso: string | null): string {
  if (!iso) return '-'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
}

function toneClass(value: number): string {
  return value < 0 ? 'w7-ws-amt-neg' : 'w7-ws-amt-pos'
}

function showMatchedTime(marketName: string): boolean {
  return (
    marketName === 'Match Odds' ||
    marketName === 'Tied Match' ||
    marketName === 'Completed Match'
  )
}

export function BetBreakdown({
  book,
  surface = 'dark',
  live = false,
  betCount,
  betFilters,
  onBetFiltersChange,
}: {
  book: WorkstationBook
  surface?: 'dark' | 'light'
  live?: boolean
  betCount?: number
  betFilters?: WorkstationBetFilters
  onBetFiltersChange?: (next: WorkstationBetFilters) => void
}) {
  const toast = useToast()
  const me = useCmMe()
  const invalidateBets = useInvalidateWorkstationBets()
  const revalidateBet = useRevalidateWorkstationBet()
  const invalidLive = live
  const canManageInvalidBets = canManageWorkstationInvalidBets(me.data?.caller)
  const serverBets = live && onBetFiltersChange
  const defaults = useMemo(() => defaultBetDateRange(), [])
  const memberOptions = useMemo(
    () => [...new Set(book.bets.map((b) => b.userName))].sort(),
    [book.bets],
  )

  const [memberNames, setMemberNames] = useState<string[]>([])
  const [memberDraft, setMemberDraft] = useState('')
  const [currency, setCurrency] = useState<string>('All')
  const [pageSize, setPageSize] = useState<number>(100)
  const [fromDate, setFromDate] = useState(defaults.fromDate)
  const [toDate, setToDate] = useState(defaults.toDate)
  const [fromDraft, setFromDraft] = useState(() => toDateInputValue(defaults.fromDate))
  const [toDraft, setToDraft] = useState(() => toDateInputValue(defaults.toDate))
  const [page, setPage] = useState(1)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set())
  const [highlightIp, setHighlightIp] = useState<string | null>(null)
  const [ipDetail, setIpDetail] = useState<WorkstationIpDetail | null>(null)
  const [ipMissing, setIpMissing] = useState(false)
  const [invalidOpen, setInvalidOpen] = useState(false)
  const [invalidReason, setInvalidReason] = useState('')
  const [revalidatingId, setRevalidatingId] = useState<string | null>(null)
  const [applied, setApplied] = useState({
    memberNames: [] as string[],
    memberQuery: '',
    currency: 'All',
    fromDate: defaults.fromDate,
    toDate: defaults.toDate,
  })

  const matchedCol = showMatchedTime(book.marketName)

  const pushLiveFilters = (next: {
    memberNames?: string[]
    memberQuery?: string
    currency?: string
    fromDate?: string
    toDate?: string
    page?: number
    maxRow?: number
  }) => {
    if (!serverBets || !betFilters || !onBetFiltersChange) return
    const names = next.memberNames ?? applied.memberNames
    const query = next.memberQuery ?? applied.memberQuery
    const userName =
      names.length > 0 ? names : query.trim() ? query.trim() : undefined
    onBetFiltersChange({
      page: next.page ?? 1,
      maxRow: next.maxRow ?? pageSize,
      fromDate: next.fromDate ?? fromDate,
      toDate: next.toDate ?? toDate,
      currencyType: next.currency ?? currency,
      userName,
    })
  }

  const filtered = useMemo(
    () => (serverBets ? book.bets : filterWorkstationBets(book.bets, applied)),
    [serverBets, book.bets, applied],
  )

  const totalRows = serverBets ? (betCount ?? book.bets.length) : filtered.length
  const pageCount = Math.max(1, Math.ceil(totalRows / pageSize))
  const safePage = serverBets ? (betFilters?.page ?? 1) : Math.min(page, pageCount)
  const rows = serverBets ? book.bets : paginateBets(filtered, safePage, pageSize)

  const openIpDetails = (ip: string) => {
    if (!ip) return
    setHighlightIp(ip)
    const detail = getWorkstationIpDetail(ip)
    if (detail) {
      setIpDetail(detail)
      setIpMissing(false)
    } else {
      setIpDetail(null)
      setIpMissing(true)
    }
  }

  const closeIpDetails = () => {
    setIpDetail(null)
    setIpMissing(false)
    setHighlightIp(null)
  }

  const resetPage = () => setPage(1)

  const applyFilters = (next: {
    memberNames?: string[]
    memberQuery?: string
    currency?: string
    fromDate?: string
    toDate?: string
  }) => {
    setApplied((prev) => ({
      memberNames: next.memberNames ?? prev.memberNames,
      memberQuery: next.memberQuery ?? prev.memberQuery,
      currency: next.currency ?? prev.currency,
      fromDate: next.fromDate ?? prev.fromDate,
      toDate: next.toDate ?? prev.toDate,
    }))
    resetPage()
    setSelectedIds(new Set())
    if (serverBets) {
      pushLiveFilters({
        memberNames: next.memberNames ?? applied.memberNames,
        memberQuery: next.memberQuery ?? applied.memberQuery,
        currency: next.currency ?? applied.currency,
        fromDate: next.fromDate ?? applied.fromDate,
        toDate: next.toDate ?? applied.toDate,
      })
    }
  }

  /** Search — chips + free-text draft (Backend-UI getBetList('search')). */
  const runSearch = () => {
    const tag = memberDraft.trim()
    let names = memberNames
    if (tag && !memberNames.includes(tag)) {
      names = [...memberNames, tag]
      setMemberNames(names)
    }
    setMemberDraft('')
    applyFilters({
      memberNames: names,
      memberQuery: tag,
      currency,
      fromDate,
      toDate,
    })
  }

  const clearDates = () => {
    const next = defaultBetDateRange()
    setFromDate(next.fromDate)
    setToDate(next.toDate)
    setFromDraft(toDateInputValue(next.fromDate))
    setToDraft(toDateInputValue(next.toDate))
    applyFilters({ fromDate: next.fromDate, toDate: next.toDate })
  }

  const commitDateDraft = (
    raw: string,
    endOfDay: boolean,
    setIso: (iso: string) => void,
    setDraft: (raw: string) => void,
    key: 'fromDate' | 'toDate',
  ) => {
    const next = fromDateInputValue(raw, endOfDay)
    if (!next) {
      setDraft(toDateInputValue(key === 'fromDate' ? fromDate : toDate))
      return
    }
    setIso(next)
    setDraft(toDateInputValue(next))
    applyFilters({ [key]: next })
  }

  const commitIso = (key: 'fromDate' | 'toDate', next: string) => {
    if (key === 'fromDate') {
      setFromDate(next)
      setFromDraft(toDateInputValue(next))
    } else {
      setToDate(next)
      setToDraft(toDateInputValue(next))
    }
    applyFilters({ [key]: next })
  }

  const addMember = (name: string) => {
    if (!name || memberNames.includes(name)) return
    const names = [...memberNames, name]
    setMemberNames(names)
    applyFilters({ memberNames: names, memberQuery: '' })
  }

  const removeMember = (name: string) => {
    const names = memberNames.filter((n) => n !== name)
    setMemberNames(names)
    applyFilters({ memberNames: names })
  }

  const toggleRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const suggestions = memberOptions.filter(
    (name) =>
      !memberNames.includes(name) &&
      (!memberDraft || name.toLowerCase().includes(memberDraft.toLowerCase())),
  )
  const selectedCount = selectedIds.size
  const trimmedReason = invalidReason.trim()
  const invalidDisabled = !live || !invalidLive || selectedCount === 0

  const closeInvalidModal = () => {
    if (invalidateBets.isPending) return
    setInvalidOpen(false)
    setInvalidReason('')
  }

  const submitInvalidBets = async () => {
    if (!live || invalidateBets.isPending) return
    if (!invalidLive) {
      toast.error(
        'Invalid bet unavailable',
        'Sign in as CM to invalidate workstation bets.',
      )
      return
    }
    if (!trimmedReason) {
      toast.error('Reason required', 'Enter why these bets should be marked invalid.')
      return
    }
    const betIds = [...selectedIds]
    if (betIds.length === 0) return

    try {
      const result = await invalidateBets.mutateAsync({ betIds, reason: trimmedReason })
      toast.success(
        result.count === 1 ? 'Bet invalidated' : 'Bets invalidated',
        `${result.count} ${result.count === 1 ? 'bet was' : 'bets were'} marked invalid.`,
      )
      setSelectedIds(new Set())
      setInvalidOpen(false)
      setInvalidReason('')
    } catch (error) {
      toast.error(
        'Could not invalidate bets',
        error instanceof Error ? error.message : 'Try again.',
      )
    }
  }

  const revalidateRow = async (betId: string) => {
    if (!live || revalidateBet.isPending || revalidatingId) return
    if (!invalidLive) {
      toast.error(
        'Revalidate bet unavailable',
        'Sign in as CM to revalidate workstation bets.',
      )
      return
    }
    setRevalidatingId(betId)
    try {
      const result = await revalidateBet.mutateAsync({ betId })
      toast.success('Bet revalidated', result.message || `${result.betId} was restored.`)
    } catch (error) {
      toast.error(
        'Could not revalidate bet',
        error instanceof Error ? error.message : 'Try again.',
      )
    } finally {
      setRevalidatingId(null)
    }
  }

  return (
    <section className="w7-ws-bets" data-ws-surface={surface} aria-label="Bet breakdown">
      <div className="w7-ws-bets-toolbar">
        {/* Row 1 — member multi-select (Backend-UI ui-select) */}
        <div className="w7-ws-bets-member">
          <div className="w7-ws-bets-chips">
            {memberNames.map((name) => (
              <button
                key={name}
                type="button"
                className="w7-ws-bets-chip"
                onClick={() => removeMember(name)}
                title="Remove"
              >
                {name} ×
              </button>
            ))}
            <input
              type="text"
              value={memberDraft}
              placeholder="Enter or Select Member Name to Search"
              list="w7-ws-member-suggestions"
              onChange={(e) => setMemberDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  if (memberDraft.trim()) {
                    addMember(memberDraft.trim())
                    setMemberDraft('')
                  } else {
                    runSearch()
                  }
                }
              }}
            />
            <datalist id="w7-ws-member-suggestions">
              {suggestions.map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>
          </div>
        </div>

        {/* Row 2 — dates, currency, max row, search */}
        <div className="w7-ws-bets-filters">
          <BetDateTimeField
            label="From Date"
            iso={fromDate}
            draft={fromDraft}
            endOfDay={false}
            onDraftChange={setFromDraft}
            onCommitIso={(next) => commitIso('fromDate', next)}
            onBlurDraft={() =>
              commitDateDraft(fromDraft, false, setFromDate, setFromDraft, 'fromDate')
            }
          />
          <BetDateTimeField
            label="To Date"
            iso={toDate}
            draft={toDraft}
            endOfDay
            onDraftChange={setToDraft}
            onCommitIso={(next) => commitIso('toDate', next)}
            onBlurDraft={() =>
              commitDateDraft(toDraft, true, setToDate, setToDraft, 'toDate')
            }
          />
          <label className="w7-ws-bets-field">
            <span>Currency Types</span>
            <select
              value={currency}
              onChange={(e) => {
                const next = e.target.value
                setCurrency(next)
                applyFilters({ currency: next })
              }}
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label className="w7-ws-bets-field">
            <span>Max Row</span>
            <select
              value={pageSize}
              onChange={(e) => {
                const next = Number(e.target.value)
                setPageSize(next)
                resetPage()
                if (serverBets) pushLiveFilters({ maxRow: next })
              }}
            >
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <div className="w7-ws-bets-actions">
            <button type="button" className="w7-ws-bets-btn" onClick={runSearch}>
              Search
            </button>
          </div>
        </div>

        {/* Row 3 — clear / invalid / count */}
        <div className="w7-ws-bets-filters w7-ws-bets-filters-secondary">
          <button type="button" className="w7-ws-bets-btn" onClick={clearDates}>
            Clear Date
          </button>
          {canManageInvalidBets && selectedCount > 0 ? (
          <button
            type="button"
            className="w7-ws-bets-btn"
            disabled={invalidDisabled}
            title={
              !live
                ? 'Invalid Bet is available only with live APIs'
                : !invalidLive
                ? 'Invalid Bet needs a live CM session'
                : selectedCount === 0
                ? 'Select bets in the table first'
                : 'Mark selected bets invalid'
            }
            onClick={() => setInvalidOpen(true)}
          >
            Invalid Bet
          </button>
          ) : null}
          <div className="w7-ws-bets-count">Bet Count : {totalRows}</div>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="w7-ws-bets-empty">No Data Found</div>
      ) : (
        <div className="w7-ws-bets-scroll">
          <table className="w7-ws-bets-table">
            <thead>
              <tr>
                <th className="w7-ws-bets-check">#</th>
                <th>Member</th>
                <th>Place time</th>
                {matchedCol ? <th>Matched Time</th> : null}
                <th>Event</th>
                <th>Market</th>
                <th>Selection</th>
                <th>Odds Placed</th>
                <th>Odds Matched</th>
                <th>Matched</th>
                <th>Unmatched</th>
                <th>Profit/Liability</th>
                <th>IP</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((bet) => (
                <BetRow
                  key={bet.id}
                  bet={bet}
                  matchedCol={matchedCol}
                  checked={selectedIds.has(bet.id)}
                  canManageInvalidBets={canManageInvalidBets}
                  live={live}
                  invalidLive={invalidLive}
                  revalidating={revalidatingId === bet.id}
                  highlightIp={highlightIp}
                  onToggle={() => toggleRow(bet.id)}
                  onRevalidate={() => void revalidateRow(bet.id)}
                  onIpDetails={openIpDetails}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalRows > 0 ? (
        <div className="w7-ws-bets-foot">
          <Pager
            page={safePage}
            pageCount={pageCount}
            onChange={(nextPage) => {
              if (serverBets && betFilters && onBetFiltersChange) {
                onBetFiltersChange({ ...betFilters, page: nextPage })
              } else {
                setPage(nextPage)
              }
            }}
            total={totalRows}
            pageSize={pageSize}
          />
        </div>
      ) : null}

      <Modal
        open={ipDetail !== null || ipMissing}
        onClose={closeIpDetails}
        title="Ip Detail"
        size="lg"
      >
        {ipMissing ? (
          <p className="w7-ws-ip-empty">No IP details found for this address.</p>
        ) : ipDetail ? (
          <IpDetailTables detail={ipDetail} />
        ) : null}
      </Modal>

      <Modal
        open={invalidOpen}
        onClose={closeInvalidModal}
        title={selectedCount === 1 ? 'Invalidate 1 bet' : `Invalidate ${selectedCount} bets`}
        subtitle={
          invalidLive
            ? 'This calls CM-API invalidRealTimeBet.'
            : 'Sign in as CM to enable the workstation invalid-bet action.'
        }
        footer={
          <>
            <span className="w7-spacer" />
            <Button variant="secondary" onClick={closeInvalidModal} disabled={invalidateBets.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              busy={invalidateBets.isPending}
              disabled={!trimmedReason}
              onClick={() => void submitInvalidBets()}
            >
              Invalid Bet
            </Button>
          </>
        }
      >
        <div className="w7-col" style={{ gap: 'var(--w7-s3)' }}>
          <p>
            Selected bets: <strong>{selectedCount}</strong>
          </p>
          <label className="w7-ws-bets-field">
            <span>Reason</span>
            <textarea
              value={invalidReason}
              rows={4}
              autoFocus
              placeholder="Enter the reason for invalidating these bets"
              onChange={(e) => setInvalidReason(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </label>
        </div>
      </Modal>
    </section>
  )
}

function BetRow({
  bet,
  matchedCol,
  checked,
  canManageInvalidBets,
  live,
  invalidLive,
  revalidating,
  highlightIp,
  onToggle,
  onRevalidate,
  onIpDetails,
}: {
  bet: WorkstationBet
  matchedCol: boolean
  checked: boolean
  canManageInvalidBets: boolean
  live: boolean
  invalidLive: boolean
  revalidating: boolean
  highlightIp: string | null
  onToggle: () => void
  onRevalidate: () => void
  onIpDetails: (ip: string) => void
}) {
  const ipActive = Boolean(highlightIp && bet.ip && bet.ip === highlightIp)
  const showRevalidate = canManageInvalidBets && canRevalidateWorkstationBet(bet)
  const showInvalidCheckbox = canManageInvalidBets && canSelectBetForInvalid(bet)
  const revalidateDisabled = revalidating || !live || !invalidLive
  return (
    <tr className={ipActive ? 'w7-ws-bets-ip-hit' : undefined}>
      <td className="w7-ws-bets-check">
        {showRevalidate ? (
          <button
            type="button"
            className="w7-ws-bets-revalid"
            title={
              !live
                ? 'Revalidate is available only with live APIs'
                : !invalidLive
                ? 'Revalidate needs a live CM session'
                : 'Re-validate Bet'
            }
            aria-label={`Re-validate bet ${bet.id}`}
            disabled={revalidateDisabled}
            onClick={onRevalidate}
          >
            <Icon name="refresh" size={14} />
          </button>
        ) : showInvalidCheckbox ? (
          <input
            type="checkbox"
            checked={checked}
            onChange={onToggle}
            aria-label={`Select ${bet.id}`}
          />
        ) : null}
      </td>
      <td>
        {bet.userName} - {bet.currencyType}
      </td>
      <td>{formatWhen(bet.placedAt)}</td>
      {matchedCol ? <td>{formatWhen(bet.matchedAt)}</td> : null}
      <td title={bet.event}>{bet.event}</td>
      <td>{bet.marketName}</td>
      <td>
        <span className={`w7-ws-bet-side w7-ws-bet-side-${bet.type.toLowerCase()}`} />
        {bet.selectionName}
        {bet.runs != null
          ? ` ${
              isLineSessionBetListMarket(bet.marketName)
                ? formatLineSessionRunsForSide(bet.runs, bet.type)
                : formatFancyRuns(bet.runs)
            }`
          : ''}
      </td>
      <td>
        {workstationOddsPlaced({
          marketName: bet.marketName,
          betRate: bet.betRate,
          runs: bet.runs,
        })}
      </td>
      <td>
        {workstationOddsMatched({
          marketName: bet.marketName,
          runs: bet.runs,
          averagePriceMatched: bet.averagePriceMatched,
          side: bet.type,
        })}
      </td>
      <td>
        {betListMatchedUnmatchedAmt(bet.matchedAmt, bet.currencyType, bet.marketName)}
      </td>
      <td>
        {betListMatchedUnmatchedAmt(bet.unMatchedAmt, bet.currencyType, bet.marketName)}
      </td>
      <td className={toneClass(bet.profitLiability)}>
        {money(bet.profitLiability, {
          round: true,
          symbol: false,
          // Lab: INR majors on Betfair Line Session only; else viewer currency.
          currency: betListProfitLiabilityCurrency(bet.currencyType, bet.marketName),
        })}
      </td>
      <td className={`w7-ws-bets-ip${ipActive ? ' is-hit' : ''}`}>
        {bet.ip ? (
          <>
            <button
              type="button"
              className="w7-ws-ip-info"
              aria-label={`IP details for ${bet.ip}`}
              title="IP details"
              onClick={() => onIpDetails(bet.ip)}
            />
            <span>{bet.ip}</span>
          </>
        ) : (
          '-'
        )}
      </td>
    </tr>
  )
}

const IP_LEFT_FIELDS: { key: keyof WorkstationIpDetail; label: string }[] = [
  { key: 'host', label: 'host' },
  { key: 'ip', label: 'ip' },
  { key: 'country_code', label: 'country_code' },
  { key: 'city', label: 'city' },
  { key: 'region', label: 'region' },
  { key: 'vpn', label: 'vpn' },
  { key: 'fraud_score', label: 'fraud_score' },
  { key: 'ISP', label: 'ISP' },
  { key: 'ASN', label: 'ASN' },
  { key: 'abuse_velocity', label: 'abuse_velocity' },
  { key: 'active_tor', label: 'active_tor' },
  { key: 'active_vpn', label: 'active_vpn' },
  { key: 'bot_status', label: 'bot_status' },
  { key: 'connection_type', label: 'connection_type' },
  { key: 'created_at', label: 'created_at' },
]

const IP_RIGHT_FIELDS: { key: keyof WorkstationIpDetail; label: string }[] = [
  { key: 'is_crawler', label: 'is_crawler' },
  { key: 'latitude', label: 'latitude' },
  { key: 'longitude', label: 'longitude' },
  { key: 'message', label: 'message' },
  { key: 'mobile', label: 'mobile' },
  { key: 'organization', label: 'organization' },
  { key: 'proxy', label: 'proxy' },
  { key: 'recent_abuse', label: 'recent_abuse' },
  { key: 'request_id', label: 'request_id' },
  { key: 'success', label: 'success' },
  { key: 'timezone', label: 'timezone' },
  { key: 'tor', label: 'tor' },
  { key: 'updated_at', label: 'updated_at' },
]

function formatIpValue(value: WorkstationIpDetail[keyof WorkstationIpDetail]): string {
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (value === '' || value == null) return '-'
  return String(value)
}

function IpDetailTables({ detail }: { detail: WorkstationIpDetail }) {
  return (
    <div className="w7-ws-ip-detail">
      <table className="w7-ws-ip-detail-table">
        <tbody>
          {IP_LEFT_FIELDS.map(({ key, label }) => (
            <tr key={key}>
              <td>{label}:</td>
              <td
                className={
                  key === 'fraud_score'
                    ? detail.fraud_score > 40
                      ? 'w7-ws-amt-neg'
                      : 'w7-ws-amt-pos'
                    : undefined
                }
              >
                {formatIpValue(detail[key])}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <table className="w7-ws-ip-detail-table">
        <tbody>
          {IP_RIGHT_FIELDS.map(({ key, label }) => (
            <tr key={key}>
              <td>{label}:</td>
              <td>{formatIpValue(detail[key])}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
